/**
 * De aangekondigde operatie, aan de kant van wie 'm ondergaat.
 *
 * Zie `docs/architecture/gelijktijdigheid.md` §7. De waarheid staat in D1 en
 * wordt gepeild; daardoor ziet ook iemand die net binnenkomt of z'n scherm
 * herlaadt waar de teller staat, in plaats van niets.
 *
 * De peiling is de enige bron. Een bericht over de verbinding zou sneller zijn,
 * maar zou een tweede weg zijn naar hetzelfde antwoord — en juist bij dit
 * onderwerp is één waarheid meer waard dan een paar seconden.
 */
import { create } from "zustand";
import { api, type PendingOperationView } from "../api/client";

/** Vaak genoeg dat een afgeblazen operatie merkbaar van het scherm verdwijnt,
 *  zeldzaam genoeg om niets te kosten. */
const POLL_MS = 10_000;

interface OperationsState {
  operations: PendingOperationView[];
  /** Operaties die deze gebruiker weg heeft geklikt nadat 'ie geantwoord had. */
  dismissed: string[];
  start: (workspaceId: string, branchId: string) => void;
  stop: () => void;
  refresh: () => Promise<void>;
  respond: (id: string, action: "ack" | "extend" | "start-now" | "cancel") => Promise<void>;
}

let timer: ReturnType<typeof setInterval> | null = null;
let scope: { workspaceId: string; branchId: string } | null = null;

export const useOperations = create<OperationsState>((set, get) => ({
  operations: [],
  dismissed: [],

  start: (workspaceId, branchId) => {
    if (scope?.workspaceId === workspaceId && scope.branchId === branchId && timer) return;
    get().stop();
    scope = { workspaceId, branchId };
    void get().refresh();
    timer = setInterval(() => void get().refresh(), POLL_MS);
  },

  stop: () => {
    if (timer) clearInterval(timer);
    timer = null;
    scope = null;
    set({ operations: [] });
  },

  refresh: async () => {
    if (!scope) return;
    try {
      const res = await api.listActiveOperations(scope.workspaceId, scope.branchId);
      // Een operatie op een set branches (restore van een plateau) is alleen
      // merkbaar op die branches; wie elders werkt hoeft de balk niet te zien.
      const hier = scope.branchId;
      set({
        operations: res.operations.filter((op) => !op.branchIds || op.branchIds.includes(hier)),
      });
    } catch {
      // Een mislukte peiling zegt niets over wat er loopt; de volgende
      // probeert het opnieuw. Het beeld laten staan is eerlijker dan 'm
      // leegmaken op grond van een netwerkfout.
    }
  },

  respond: async (id, action) => {
    if (!scope) return;
    await api.respondToOperation(scope.workspaceId, scope.branchId, id, action);
    await get().refresh();
  },
}));
